/** KEYS — QdrantMapper client-key management (spec §6). Data via the
 *  session-gated /admin/mapper-keys routes; full key values exist only inside
 *  the once-only modal and are never re-fetchable, never persisted to
 *  localStorage/sessionStorage, and never placed in the URL — they live only
 *  in this component's React state and vanish when the modal is dismissed. */
import { useCallback, useEffect, useState } from 'react'
import { get, post } from '../api/client'
import { MapperKeyCreated, MapperKeyEntity, MapperKeysResponse } from '../api/schemas'
import type { MapperKeyRow } from '../api/schemas'

type Row = MapperKeyRow

/** Confirmation state is a single `"<kind>:<id>"` tag, not a boolean, so the
 *  armed state is bound to one exact row+action pair. That is also what
 *  keeps a pending confirmation from ever landing on the wrong key: clicking
 *  ANY button whose own tag does not match the currently-armed tag falls
 *  into the `!==` branch below and re-arms itself instead of executing —
 *  never runs the action that was armed for a different row or a different
 *  button. Switching rows, switching from revoke to rotate on the same row,
 *  or firing an unrelated action (create) all leave the previous tag unable
 *  to match anything but its own exact button. */
type Confirming = `${'revoke' | 'rotate'}:${string}` | null

/** Mirrors the backend's own `_NAME_RE` in collector/api/mapper_admin.py
 *  exactly. Gating Create on it client-side is pure UX -- the server is the
 *  actual authority and still validates on every request -- but it means an
 *  obviously-bad name (spaces, capitals, punctuation) never has to round-trip
 *  to a 422 to be rejected. */
const NAME_RE = /^[a-z0-9-]{1,64}$/

/** Shown whenever the typed name fails NAME_RE, and as the disabled button's
 *  tooltip. A silently-disabled control is a dead end; this is the sentence
 *  that turns it back into a correctable mistake. */
const NAME_RULE = 'Name must be lowercase letters, digits or hyphens only — no spaces or capitals.'

export function QdrantKeys() {
  const [rows, setRows] = useState<Row[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [note, setNote] = useState('')
  const nameValid = NAME_RE.test(name.trim())
  const [minted, setMinted] = useState<{ name: string; key: string } | null>(null)
  const [confirming, setConfirming] = useState<Confirming>(null)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'manual'>('idle')

  const load = useCallback(async () => {
    try {
      const res = await get('/admin/mapper-keys', MapperKeysResponse)
      setRows(res.keys)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed')
    }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
  }, [load])

  async function createKey() {
    try {
      const made = await post('/admin/mapper-keys', MapperKeyCreated,
                               { name: name.trim(), note: note.trim() || null })
      setMinted({ name: made.name, key: made.key })
      setCopyState('idle')
      setName(''); setNote('')
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'create failed')
    }
  }

  async function act(kind: 'revoke' | 'rotate', id: string) {
    const tag: Confirming = `${kind}:${id}`
    if (confirming !== tag) { setConfirming(tag); return }
    setConfirming(null)
    try {
      if (kind === 'rotate') {
        const made = await post(`/admin/mapper-keys/${id}/rotate`, MapperKeyCreated, {})
        setMinted({ name: made.name, key: made.key })
        setCopyState('idle')
      } else {
        await post(`/admin/mapper-keys/${id}/revoke`, MapperKeyEntity, {})
      }
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : `${kind} failed`)
    }
  }

  /** `navigator.clipboard` exists only in secure contexts (HTTPS or
   *  localhost). This dashboard currently runs on plain HTTP at
   *  http://152.42.176.211:12002 (known, accepted -- TLS is a tracked
   *  follow-up), so `navigator.clipboard` is `undefined` there today and
   *  a bare `.writeText(...)` call throws synchronously with no visible
   *  error -- an operator clicks Copy, sees nothing wrong, clicks Done,
   *  and the once-only key value is gone for good. Guard both failure
   *  shapes -- the missing API (optional chaining short-circuits to
   *  `undefined`, not a call) and a rejected promise (permission denied,
   *  browser policy) -- and fall back to telling the operator to select
   *  and copy the `<code>` value manually; it already has `user-select:
   *  all` so a single click selects the whole key. */
  async function copyKey(key: string) {
    if (!navigator.clipboard) {
      setCopyState('manual')
      return
    }
    try {
      await navigator.clipboard.writeText(key)
      setCopyState('copied')
    } catch {
      // Rejected promise -- e.g. permissions-policy denied the call even
      // though the API object exists. Same visible fallback as the
      // missing-API case: tell the operator to select and copy by hand.
      setCopyState('manual')
    }
  }

  return (
    <section className="qd-keys">
      {/* No internal heading here -- App.tsx now wraps this panel in its own
          <Section id="sec-qd-keys" title="Keys" .../>, matching every other
          Qdrant sub-panel's convention (fix round 1, Required 2); a second
          "KEYS" label here would just repeat that one immediately above it. */}
      {error && <p className="qd-keys-error">{error}</p>}
      {/* The name rule is enforced in THREE places and only one of them is
          visible, which is how this went wrong the first time: the button was
          disabled on NAME_RE with nothing but a placeholder to explain it, and
          the placeholder disappears the moment you type. Someone entering
          "My Key" saw a dead button and no reason for it. Keep the disable --
          it saves a pointless 422 round-trip -- but always say why. */}
      <div className="qd-keys-create">
        <input placeholder="name (a-z 0-9 -)" value={name}
               aria-invalid={name.trim() !== '' && !nameValid}
               onChange={(e) => setName(e.target.value)} />
        <input placeholder="note (optional)" value={note}
               onChange={(e) => setNote(e.target.value)} />
        <button disabled={!nameValid} onClick={createKey}
                title={nameValid ? 'Create key' : NAME_RULE}>Create key</button>
      </div>
      {name.trim() !== '' && !nameValid && (
        <p className="qd-keys-hint" role="alert">{NAME_RULE}</p>
      )}
      <table className="t">
        <thead><tr><th>name</th><th>key</th><th>status</th><th>created</th>
                   <th>last seen</th><th /></tr></thead>
        <tbody>
          {(rows ?? []).map((k) => (
            <tr key={k.id} className={k.status === 'revoked' ? 'qd-key-revoked' : ''}>
              <td>{k.name}</td>
              <td className="qd-hint">{k.hint}</td>
              <td>{k.status}</td>
              <td>{k.created_at.slice(0, 10)}</td>
              <td>{k.last_seen ? k.last_seen.slice(0, 16).replace('T', ' ') : '—'}</td>
              <td>
                {k.status === 'active' && (
                  <>
                    <button className={confirming === `rotate:${k.id}` ? 'qd-confirm' : ''}
                            onClick={() => act('rotate', k.id)}>
                      {confirming === `rotate:${k.id}` ? 'confirm rotate?' : 'Rotate'}
                    </button>
                    <button className={confirming === `revoke:${k.id}` ? 'qd-confirm' : ''}
                            onClick={() => act('revoke', k.id)}>
                      {confirming === `revoke:${k.id}` ? 'confirm?' : 'Revoke'}
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {minted && (
        <div className="qd-key-modal" role="dialog">
          <div className="qd-key-modal-card">
            <h4>Key for {minted.name}</h4>
            <code className="qd-key-value">{minted.key}</code>
            <p>Copy it now — you won't see this value again.</p>
            {copyState === 'copied' && <p className="qd-key-copy-state">Copied.</p>}
            {copyState === 'manual' && (
              <p className="qd-key-copy-state qd-key-copy-manual">
                Couldn't copy automatically — click the key above to select it, then copy manually (Cmd/Ctrl+C).
              </p>
            )}
            <button onClick={() => copyKey(minted.key)}>Copy</button>
            <button onClick={() => { setMinted(null); setCopyState('idle') }}>Done</button>
          </div>
        </div>
      )}
    </section>
  )
}
